import { defineStore } from "pinia";
import { IBrand, ICampaign, IProductColor, IResponseStore, IStore, IStoreProfitPercentage } from "@/interfaces/StoreInterfaces";
import { IPayableInfo, IPointsWithdrawalRequestVariable, PayableInfoQueryResponse } from "@/interfaces/PointInterfaces";
import { INITIAL_PAYABLE_INFO } from "@/Constants/PointsConstants";
import { INITIAL_SELECTED_STORE, INITIAL_STORE_PROFIT_PERCENTAGE } from "@/Constants/StoreConstants";
import ApolloClient from "@/apollo-client";
import selectedStore from "@/Graphql/Stores/GetSelectedStore.query.graphql";
import StoreProfitPercentage from "@/Graphql/Stores/GetStoreProfitPercentage.query.graphql";
import updateStoreProfitPercentage from "@/Graphql/Stores/UpdateStoreProfitPercentage.mutation.graphql";
import allStores from "@/Graphql/Stores/Stores.query.graphql";
import checkStoreSubscriptionStatus from "@/Graphql/Welcome/CheckStoreSubscription.query.graphql";
import getPayableInfo from "@/Graphql/Points/Queries/GetPayableInfo.query.graphql";
import getPayableInfoByStoreScope from "@/Graphql/Points/Queries/GetPayableInfoByStoreScope.query.graphql";
import pointsWithdrawalRequestMutation from "@/Graphql/Points/Mutations/storePointsWithdrawalRequest.mutation.graphql";
import { useAuthStore } from "./AuthStore";

export interface IActivity {
  scopeId: number;
  flag: boolean;
  count: number;
}

interface IStoreSummary {
  orders: number;
  points: number;
  sales: number;
  discountAmount: number;
}

interface IStoreProfitPercentageIncoming {
  points_percentage: string;
  updated_at: string;
  updated_product_price_percentage: string;
}

interface IMainStoreState {
  currentStoreTab: string;
  currentSidebarOption: string;
  selectedStore: IResponseStore;
  campaignIds: number[];
  allStoreIds: number[];
  allStores: IStore[];
  storeSummary: IStoreSummary;
  isLoading: boolean;
  isSubscribed: boolean;
  campaigns: ICampaign[];
  allColors: IProductColor[];
  allBrands: IBrand[];
  payableInfo: IPayableInfo;
  requestPayableInfo: IPayableInfo | null;
  isPrimaryPayable: boolean | null;
  storeProfitPercentage: IStoreProfitPercentage;
}
interface ICheckStoreSubscriptionResult {
  storeSubscribedUser: {
    isSubscribed: boolean;
    exceptionCode?: number;
    message?: string;
    type?: string;
  };
}
export const useMainStore = defineStore({
  id: "main",
  state: (): IMainStoreState => ({
    currentStoreTab: "overview",
    currentSidebarOption: "stores",
    selectedStore: { ...INITIAL_SELECTED_STORE },
    campaignIds: [],
    campaigns: [],
    allStoreIds: [],
    allStores: [],
    storeSummary: {
      orders: 0,
      points: 0,
      sales: 0,
      discountAmount: 0,
    },
    isLoading: true,
    isSubscribed: false,
    allColors: [],
    allBrands: [],
    payableInfo: { ...INITIAL_PAYABLE_INFO },
    isPrimaryPayable: null,
    requestPayableInfo: null,
    storeProfitPercentage: {
      ...INITIAL_STORE_PROFIT_PERCENTAGE,
    },
  }),
  getters: {
    getCurrentStoreTab: (state): string => state.currentStoreTab,
    getCurrentSidebarOption: (state): string => state.currentSidebarOption,
    getSelectedStore: (state): IResponseStore => state.selectedStore,
    getAllStoreIds: (state): number[] => state.allStoreIds,
    getAllStores: (state): IStore[] => state.allStores,
    getCampaignIds: (state): number[] => state.campaignIds,
    getStoresSummary: (state): IStoreSummary => state.storeSummary,
    getAllColors: (state): IProductColor[] => state.allColors,
    getAllBrands: (state): IBrand[] => state.allBrands,
    getPayableInfo: (state): IPayableInfo => state.payableInfo,
    getRequestPayableInfo: (state): IPayableInfo | null => state.requestPayableInfo,
    getIsPrimaryPayable: (state): boolean | null => state.isPrimaryPayable,
    getStoreProfitPercentage: (state): Partial<IStoreProfitPercentage> => {
      const { defaultProductPricePercentage, lastUpdated, productPricePercentage } = state.storeProfitPercentage;
      return {
        defaultProductPricePercentage,
        lastUpdated,
        productPricePercentage,
      };
    },
  },
  actions: {
    setLoadingState(payload: boolean) {
      this.isLoading = payload;
    },
    setCurrentStoreTab(payload: string) {
      this.currentStoreTab = payload;
    },
    setCurrentSidebarOption(payload: string) {
      this.currentSidebarOption = payload;
    },
    setCampaignIds(payload: number[]) {
      this.campaignIds = payload;
    },
    setCampaigns(payload: ICampaign[]) {
      this.campaigns = payload;
    },
    setSelectedStore(payload: IResponseStore) {
      this.selectedStore = payload;
    },
    setSelectedStoreStatus(payload: { enabled: boolean }) {
      const selectedStoreReplica = { ...this.selectedStore };
      selectedStoreReplica.enabled = payload.enabled;
      this.setSelectedStore(selectedStoreReplica);
    },
    setAllStoreIds(payload: number[]) {
      this.allStoreIds = payload;
    },
    setAllStores(payload: IStore[]) {
      this.allStores = payload;
    },
    setStoresSummary(payload: IStoreSummary) {
      this.storeSummary = payload;
    },
    setAllBrands(payload: IBrand[]) {
      this.allBrands = payload;
    },
    setAllColors(payload: IProductColor[]) {
      this.allColors = payload;
    },
    setPayableInfo(payload: IPayableInfo) {
      this.payableInfo = payload;
    },
    setRequestPayableInfo(payload: IPayableInfo | null) {
      this.requestPayableInfo = payload;
    },
    setIsPrimaryPayable(value: boolean | null) {
      this.isPrimaryPayable = value;
    },
    setProductPercentage(profitPercentage: IStoreProfitPercentageIncoming) {
      this.storeProfitPercentage = {
        productPricePercentage: profitPercentage.updated_product_price_percentage,
        defaultProductPricePercentage: profitPercentage.points_percentage,
        lastUpdated: profitPercentage.updated_at,
      };
    },
    async fetchStoreProfitPercentage(id: number) {
      try {
        const {
          data: { storeProfitPercentage },
        } = await ApolloClient.query({
          query: StoreProfitPercentage,
          variables: { storeId: id },
          fetchPolicy: "network-only",
        });
        this.setProductPercentage(storeProfitPercentage);
      } catch (error) {
        this.setSelectedStore({ ...INITIAL_SELECTED_STORE });
      }
    },
    async updateStoreProfitPercentage(storeId: number, updatedProductPricePercentage: number) {
      try {
        const {
          data: { storeProfitPercentageUpdate },
        } = await ApolloClient.mutate({
          mutation: updateStoreProfitPercentage,
          variables: {
            storeId,
            updatedProductPricePercentage,
          },
        });
        this.storeProfitPercentage.productPricePercentage = storeProfitPercentageUpdate.updated_product_price_percentage;
        this.storeProfitPercentage.lastUpdated = Date.now().toString();
      } catch (error) {
        this.setSelectedStore({ ...INITIAL_SELECTED_STORE });
      }
    },
    async fetchSelectedStore(id: number) {
      try {
        const {
          data: { storeByScopeId },
        } = await ApolloClient.query({
          query: selectedStore,
          variables: { scopeId: id },
          fetchPolicy: "network-only",
        });
        this.setSelectedStore(storeByScopeId);
      } catch (error) {
        this.setSelectedStore(INITIAL_SELECTED_STORE);
      }
    },
    async fetchAllStores() {
      try {
        const authStore = useAuthStore();
        const { data } = await ApolloClient.query({
          query: allStores,
          variables: {
            input: {
              all: true,
              email: authStore.getCurrentUser.email,
              limit: 0,
              offset: 0,
              scopeIds: [],
            },
          },
          fetchPolicy: "network-only",
        });
        if (data?.storesByGLEmail) {
          if (data.storesByGLEmail?.stores) {
            this.setAllStoreIds(
              data.storesByGLEmail.stores.map(({ entityId }: { entityId: number }) => {
                return entityId;
              })
            );
            this.setAllStores(
              data.storesByGLEmail.stores.map(
                ({ storeId, entityId, name, teamName }: { storeId: number; entityId: number; name: string; teamName: string }) => {
                  return {
                    storeId,
                    entityId,
                    name,
                    teamName,
                  };
                }
              )
            );
          } else if (data.storesByGLEmail.exceptionCode) {
            this.setAllStoreIds([]);
            this.setAllStores([]);
          }
        }
      } catch (error) {
        this.setAllStoreIds([]);
        this.setAllStores([]);
      }
    },
    async checkStoreSubscriptionStatus() {
      try {
        const { data }: { data: ICheckStoreSubscriptionResult } = await ApolloClient.query({
          query: checkStoreSubscriptionStatus,
          fetchPolicy: "network-only",
        });
        this.isSubscribed = data?.storeSubscribedUser?.isSubscribed ?? false;
      } catch (error) {
        this.isSubscribed = false;
      }
    },
    async fetchPayableInfo(input: { campaignId: number; primary: boolean }): Promise<PayableInfoQueryResponse> {
      try {
        const { data } = await ApolloClient.query({
          query: getPayableInfo,
          variables: {
            input,
          },
          fetchPolicy: "network-only",
        });
        const EXCEPTION_NOT_FOUND = 404;
        if (data?.storeCampaignPayableInfo?.exceptionCode === EXCEPTION_NOT_FOUND) {
          this.setPayableInfo({ ...INITIAL_PAYABLE_INFO });
          this.setIsPrimaryPayable(null);
          await this.fetchPayableInfo({ campaignId: input.campaignId, primary: true }); // fetch data from raise db
        } else if (data.storeCampaignPayableInfo.exceptionCode === undefined) {
          const payableInfo = data.storeCampaignPayableInfo;
          this.setPayableInfo(payableInfo);
          this.setRequestPayableInfo(null);
          this.setIsPrimaryPayable(input.primary);
        }
        return data;
      } catch (error) {
        this.setPayableInfo({ ...INITIAL_PAYABLE_INFO });
        this.setIsPrimaryPayable(null);
        throw error;
      }
    },
    async fetchPayableInfoByScopeId() {
      try {
        const { data } = await ApolloClient.query({
          query: getPayableInfoByStoreScope,
          variables: {
            scopeId: this.selectedStore.entityId,
          },
          fetchPolicy: "network-only",
        });

        if (data?.storeScopePayableInfo?.exceptionCode) {
          this.setPayableInfo({ ...INITIAL_PAYABLE_INFO });
          this.setIsPrimaryPayable(null);
        } else {
          const payableInfo = data.storeScopePayableInfo;
          this.setPayableInfo(payableInfo);
        }
        return data;
      } catch (error) {
        this.setPayableInfo({ ...INITIAL_PAYABLE_INFO });
        this.setIsPrimaryPayable(null);
        return error;
      }
    },
    async requestPointsWithdrawal(payload: IPointsWithdrawalRequestVariable) {
      try {
        const { data } = await ApolloClient.mutate({
          mutation: pointsWithdrawalRequestMutation,
          variables: {
            input: payload,
          },
        });
        if (data.storePointsWithdrawalRequest) {
          if (data.storePointsWithdrawalRequest.exceptionCode) {
            throw "Err! Request failed";
          } else {
            this.selectedStore.earnedPointsBalance = this.selectedStore.earnedPointsBalance - Number(data?.storePointsWithdrawalRequest?.amount);
            this.selectedStore.totalPoints = this.selectedStore.totalPoints - Number(data?.storePointsWithdrawalRequest?.amount);
          }
        }
      } catch (error) {
        return error;
      }
    },
  },
  persist: {
    afterRestore: (ctx) => {
      ctx.store?.setRequestPayableInfo(null);
      ctx.store?.setIsPrimaryPayable(null);
    },
  },
});
